<template>
  <div class="step padding-top-ml">
    <div class="content">
      <div class="ri-medium-bold-headline">
        עוד רגע וסיימנו!
      </div>
      <div class="ri-large-body margin-top-xxs">
        האתגר קורה בווטסאפ, בשביל זה צריך את מספר הטלפון שלך.
      </div>

      <text-input
        class="margin-top-l"
        :label-text="'מספר טלפון נייד'"
        :value="phoneNumber"
        :on-input="setPhoneNumber"
        :input-type="'number'"
        :error="!!errorLabel"
        :top-left-label="errorLabel"
      />

      <div class="ri-large-body margin-top-xxs margin-bottom-l">
        מיד נשלח לך קוד אימות ב-WhatsApp לוודא שההרשמה בוצעה בהצלחה.
      </div>

      <check-box
        :initially-checked="termsAndConditionsAgreed"
        :toggle="toggleTermsAndConditions"
      >
        <template v-slot:title>
          <div class="checkbox-title">
            קראתי ואישרתי את
            <span
              class="link"
              @click="openTerms()"
            >תנאי השימוש</span>
            <span
              class="link margin-right-xxs"
              @click="openPrivacy()"
            >ותנאי הפרטיות</span>
          </div>
        </template>
      </check-box>
      <check-box
        :initially-checked="enableMarketingEmails"
        :toggle="toggleEnableMarketingEmails"
      >
        <template v-slot:title>
          <div class="checkbox-title">
            אשמח לקבל עדכונים, טיפים וחדשות למייל
          </div>
        </template>
      </check-box>
      <check-box
        :initially-checked="whatsappAgreed"
        :toggle="toggleWhatsappAgreed"
      >
        <template v-slot:title>
          <div class="checkbox-title">
            אסכים לקבל מכם WhatsApp
          </div>
        </template>
      </check-box>
      <base-tool-tip
        class="margin-top-ml"
        :icon-path="heartIcon"
      >
        <div class="whatsapp-text">
          {{ toolTipText }}
        </div>
      </base-tool-tip>
      <div>
        <riseup-button
          class="next-button margin-top-l"
          :disabled="disabled || loading || authLoading"
          :title="'לסיום ההרשמה'"
          :loading="loading || authLoading"
          size="small"
          :action="nextClicked"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>

import _ from 'lodash';
import TextInput from '@riseupil/base-ui/src/components/Inputs/TextInput.vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import RiseupButton from '@riseupil/base-ui/src/components/RiseupButton/RiseupButton.vue';
import BaseToolTip from '../../Common/BaseToolTip.vue';
import Segment from '../../../Segment';
import CheckBox from './CheckBox.vue';
import { useSignupStore } from '../../../stores/signup';
import { useAuthenticationStore } from '../../../stores/authentication';
import heartIcon from '../../../assets/heart.svg';
import { parseAuthenticationError } from '../../../utils/LoginErrorParser';

const errorLabel = ref<string>('');
const signupStore = useSignupStore();
const authenticationStore = useAuthenticationStore();
const { phoneNumber, enableMarketingEmails, loading } = storeToRefs(signupStore);
const { loading: authLoading } = storeToRefs(authenticationStore);

const termsAndConditionsAgreed = ref(false);
const whatsappAgreed = ref(true);

const disabled = computed(() => _.isEmpty(_.trim(phoneNumber.value)) || !!errorLabel.value || !termsAndConditionsAgreed.value
    || !whatsappAgreed.value);
const toolTipText = 'ההרשמה לאתגר כוללת קבלת הודעות ועדכונים ב WhatsApp.';

Segment.trackUserGot('Signup_EnterPhoneNumber_Shown');
function setPhoneNumber(value: string) {
  errorLabel.value = '';
  signupStore.setPhoneNumber(value);
}
function openTerms() {
  Segment.trackUserInteraction('OpenTermAndConditions_Clicked');
  window.open('https://www.riseup.co.il/termsofuse', '_blank');
}
function openPrivacy() {
  Segment.trackUserInteraction('OpenPrivacy_Clicked');
  window.open('https://www.riseup.co.il/privacy-policy', '_blank');
}
function toggleTermsAndConditions() {
  termsAndConditionsAgreed.value = !termsAndConditionsAgreed.value;
}
function toggleWhatsappAgreed() {
  whatsappAgreed.value = !whatsappAgreed.value;
}

function toggleEnableMarketingEmails() {
  signupStore.setEnableMarketingEmails(!enableMarketingEmails.value);
}
async function nextClicked() {
  Segment.trackUserInteraction(
    'Signup_EnterPhoneNumber_NextClicked',
    { phoneNumber: phoneNumber.value, enableMarketingEmails: enableMarketingEmails.value },
  );
  try {
    await authenticationStore.generateCode(phoneNumber.value);
    signupStore.nextStep();
  } catch (e) {
    errorLabel.value = parseAuthenticationError(e);
    Segment.trackUserGot('Signup_EnterPhoneNumber_ErrorGeneratingCode', { errorLabel: errorLabel.value });
  }
}

</script>
<style lang="scss" scoped>
@import '@riseupil/base-ui/src/scss/riseup-colors';
@import '@riseupil/base-ui/src/scss/mixins';
@import '@riseupil/base-ui/src/scss/spacings';

.step {
  @include white-page-frame;
  @include responsive-padding;
  flex: 1;
  width: 100%;

  .content {
    width: 100%;
  }
  .checkboxes {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  .checkbox-title {
    font-size: 14px;
    margin-right: $ri-spacing-sm;

    .link {
      color: $riseup_blue;
      cursor: pointer;
    }
  }
  .whatsapp-text {
    font-size: 14px;
    line-height: 20px;
  }
  .next-button {
    height: 58px;
    width: 100%;
  }

}
</style>
